import type { ComponentInit } from "@scripts/registry";

import { SEARCH_BAR_MIN_LENGTH } from "./SearchBar.types";

const INPUT = "[data-fa-search-bar-input]";
const SUBMIT = "[data-fa-search-bar-submit]";
const FILLED = "fa-search-bar--filled";

export const initSearchBar: ComponentInit = (element) => {
	const form = element as HTMLFormElement;
	const input = form.querySelector<HTMLInputElement>(INPUT);
	const submit = form.querySelector<HTMLElement>(SUBMIT);

	if (!input || !submit) return undefined;

	const declared = Number.parseInt(form.dataset.faSearchBarMin ?? "", 10);
	const min = Number.isInteger(declared) && declared > 0 ? declared : SEARCH_BAR_MIN_LENGTH;

	const initialDisabled = submit.getAttribute("aria-disabled");
	const controller = new AbortController();

	const sync = () => {
		const met = input.value.trim().length >= min;

		form.classList.toggle(FILLED, met);

		if (met) submit.removeAttribute("aria-disabled");
		else submit.setAttribute("aria-disabled", "true");
	};

	sync();

	input.addEventListener("input", sync, { signal: controller.signal });

	/* Keyed on focus, not on `event.submitter`: implicit submission names the default button
	   as the submitter too, so Enter in the field is indistinguishable from activating it. */
	form.addEventListener(
		"submit",
		(event) => {
			if (document.activeElement !== submit) return;
			if (input.value.trim().length < min) event.preventDefault();
		},
		{ signal: controller.signal },
	);

	/* `reset` restores the default value, not an empty one, so a pre-filled bar would clear
	   back to its own query. It also fires before the value changes, hence the tick. */
	form.addEventListener(
		"reset",
		() => {
			window.setTimeout(() => {
				input.value = "";
				input.focus();
				sync();
			}, 0);
		},
		{ signal: controller.signal },
	);

	return () => {
		controller.abort();
		form.classList.remove(FILLED);

		if (initialDisabled === null) submit.removeAttribute("aria-disabled");
		else submit.setAttribute("aria-disabled", initialDisabled);
	};
};
